import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, Sun } from "lucide-react";
import { TarotCard, CardBack } from "../components/TarotCard";
import { ArcanaIcon } from "../components/ArcanaIcon";
import { QUESTIONS } from "../data/quiz";
import { AceHeart } from "../components/AceHeart";
import { ProfileSetup } from "../components/ProfileSetup";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const PHRASES = [
  "« Il suffit parfois d'un instant pour que tout commence. »",
  "« Votre histoire ne tient peut-être qu'à trois cartes. »",
  "« Certaines personnes éveillent quelque chose que l'on ne cherchait même plus. »",
  "« Et si votre évidence était à quelques clics ? »",
];

// Tarif unique du test : 10 €
const TEST_PRICE = "10 €";

const Paywall = ({ retake = false }) => {
  const [loading, setLoading] = useState(false);
  const [promo, setPromo] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const { setUser } = useAuth();
  const applyCode = async () => {
    if (!promo.trim()) return;
    setPromoLoading(true);
    try {
      const { data } = await api.post("/promo/apply", { code: promo });
      const me = await api.get("/auth/me");
      setUser(me.data);
      toast.success(data.retake ? "Code accepté — vous pouvez refaire le test !" : "Code accepté — votre test est offert !");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPromoLoading(false);
    }
  };
  const unlock = async (retake = true) => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, retake });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };
  return (
    <div className="relative flex min-h-[calc(100vh-64px)] items-center justify-center overflow-hidden px-4 py-16" data-testid="paywall">
      <img src="/assets/mandala-sun.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-b from-cream via-cream/50 to-cream" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md rounded-3xl border border-gold/50 bg-white/90 p-10 text-center shadow-card backdrop-blur-md"
      >
        <div className="mx-auto h-44 w-32">
          <CardBack variant={1} />
        </div>
        <h1 className="mt-6 font-serif text-2xl font-medium text-ink">{retake ? "Prolongez l'aventure…" : "Débloquez votre test de compatibilité"}</h1>
        <p className="mt-3 text-sm leading-relaxed text-clay">
          {retake
            ? "Pour recevoir de nouveaux matchs, prolongez votre accès d'un mois puis découvrez vos nouveaux matchs ! Vous pouvez refaire le test si vous le souhaitez, mais aucune obligation — vos réponses sont enregistrées."
            : "Dix questions sur votre personnalité, vos valeurs et votre façon d'aimer. Sous chacune, quatre cartes à retourner — et vos réponses révèlent les profils qui vous correspondent vraiment."}
        </p>
        <p className="mt-6 font-serif text-5xl font-semibold text-ink" data-testid="paywall-price">10 €</p>
        <p className="mt-3 rounded-xl bg-gold-light/60 px-4 py-2.5 text-xs leading-relaxed text-ink" data-testid="paywall-monthly-note">
          Votre test débloque <strong>1 mois complet</strong> de rencontres : profils compatibles, matchs et messagerie inclus.
        </p>
        <button
          onClick={() => unlock(!retake)}
          disabled={loading}
          data-testid="paywall-unlock-button"
          className="mt-5 w-full rounded-full bg-terra px-6 py-3.5 text-sm font-semibold text-cream shadow-boho transition-all duration-300 hover:bg-terra-dark disabled:opacity-60"
        >
          {loading ? "Redirection vers le paiement…" : retake ? `Prolonger mon accès — ${TEST_PRICE}` : "Débloquer mon test"}
        </button>
        {retake && (
          <button
            onClick={() => unlock(true)}
            disabled={loading}
            data-testid="paywall-retake-button"
            className="mt-3 w-full rounded-full border border-terra/40 bg-white/70 px-6 py-3 text-sm font-semibold text-terra transition-all duration-300 hover:border-terra hover:bg-white disabled:opacity-60"
          >
            Prolonger et refaire le test — {TEST_PRICE}
          </button>
        )}
        <p className="mt-3 text-[11px] text-stone2">Réservé aux 18 ans et plus · Paiement sécurisé par Stripe{retake ? " — sans abonnement, vos réponses sont gardées" : ""}</p>
        <div className="mt-5 border-t border-terra/10 pt-5">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terra">Un code d'invitation ?</p>
          <div className="mt-2 flex gap-2">
            <input
              value={promo}
              onChange={(e) => setPromo(e.target.value)}
              data-testid="paywall-promo-input"
              placeholder="Ex : COEUR2026"
              className="flex-1 rounded-xl border border-terra/20 bg-cream px-4 py-2.5 text-sm uppercase text-ink outline-none focus:border-terra"
            />
            <button
              onClick={applyCode}
              disabled={promoLoading}
              data-testid="paywall-promo-button"
              className="rounded-xl bg-plum px-4 py-2.5 text-sm font-semibold text-cream transition-colors hover:bg-ink disabled:opacity-50"
            >
              Valider
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default function Quiz({ draft = false }) {
  const [step, setStep] = useState(0);
  const [flipped, setFlipped] = useState(() => QUESTIONS[0].cards.map(() => false));
  const [selected, setSelected] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const q = QUESTIONS[step];

  // Accès prolongé sans refaire le test : rien à refaire, direction Découvrir
  useEffect(() => {
    if (!draft && !result && user && user.role !== "admin" && user.archetype && !user.retake_credit && user.match_access_active) {
      navigate("/discover");
    }
  }, [user, draft, result, navigate]);

  // À chaque nouvelle question (ou résultat), remonter en haut pour lire la question
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step, result]);

  // Un seul clic sur n'importe quelle carte retourne les 4 cartes d'un coup.
  // Ensuite, un clic sur une carte révélée la sélectionne / désélectionne (2 max, la nouvelle remplace la plus ancienne).
  const handleCard = (i) => {
    if (!flipped.every(Boolean)) {
      setFlipped((f) => f.map(() => true));
      return;
    }
    setSelected((s) =>
      s.includes(i) ? s.filter((x) => x !== i) : s.length >= 2 ? [...s.slice(1), i] : [...s, i]
    );
  };

  const validate = async () => {
    if (selected.length === 0) {
      toast.info("Touchez d'abord 1 ou 2 cartes retournées pour les choisir.");
      return;
    }
    const chosen = selected.map((i) => q.cards[i]);
    const entry = {
      question: q.question,
      picks: chosen.map((c) => `${c.arcana} — ${c.answer}`),
      aff: chosen.map((c) => c.affinity),
    };
    const newAnswers = [...answers, entry];
    if (step < QUESTIONS.length - 1) {
      setAnswers(newAnswers);
      setStep(step + 1);
      setFlipped(QUESTIONS[step + 1].cards.map(() => false));
      setSelected([]);
      return;
    }
    setSubmitting(true);
    try {
      const affinities = newAnswers.flatMap((a) => a.aff);
      const { data } = await api.post(draft ? "/quiz/preview" : "/profile/quiz", { affinities, answers: newAnswers });
      setResult(data);
      if (user) setUser({ ...user, archetype: data.archetype, retake_credit: false });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const isDraftPreview = draft || (user && user.role === "admin");

  if (!draft && !result && user && user.role !== "admin") {
    if (!user.paid) {
      return <Paywall />;
    }
    if (user.archetype && !user.retake_credit) {
      return <Paywall retake />;
    }
  }

  if (result) {
    // Photo de profil obligatoire avant de dévoiler le résultat (anti faux profils)
    if (!draft && user && user.role !== "admin" && !user.avatar) {
      return <ProfileSetup user={user} setUser={setUser} />;
    }
    return (
      <div className="relative flex min-h-[calc(100vh-64px)] items-center justify-center overflow-hidden px-4 py-16" data-testid="quiz-result">
        <img src="/assets/mandala-sun.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.22]" />
        <div className="absolute inset-0 bg-gradient-to-b from-rosedust/80 via-cream/70 to-rosedust/80" />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full max-w-lg rounded-3xl border-2 border-gold/60 bg-white/95 p-10 text-center shadow-card backdrop-blur-md"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-terra">Votre façon d'aimer</p>
          <div className="relative mx-auto mt-8 w-fit">
            <div className="absolute -left-16 top-4 hidden h-52 w-32 -rotate-12 overflow-hidden rounded-xl border border-gold/40 opacity-70 shadow-card sm:block">
              <img src="/assets/card-back-lune-sm.jpeg" alt="" className="h-full w-full object-cover" />
            </div>
            <div className="absolute -right-16 top-4 hidden h-52 w-32 rotate-12 overflow-hidden rounded-xl border border-gold/40 opacity-70 shadow-card sm:block">
              <img src="/assets/card-back-soleil-sm.jpeg" alt="" className="h-full w-full object-cover" />
            </div>
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative z-10 flex h-64 w-44 flex-col items-center justify-center rounded-2xl border-2 border-gold/70 bg-[#5C1E28] p-4 shadow-card"
            >
              <span className="font-cinzel text-lg tracking-[0.3em] text-gold" data-testid="result-numeral">{result.numeral}</span>
              <ArcanaIcon name={result.icon} size={72} className="my-4 text-gold-light" />
              <span className="font-serif text-xl font-semibold leading-tight text-cream" data-testid="result-archetype">{result.label}</span>
            </motion.div>
          </div>
          <p className="mt-8 text-sm leading-relaxed text-clay" data-testid="result-description">{result.description}</p>
          <p className="mt-4 rounded-2xl bg-rosedust/40 px-5 py-4 text-xs leading-relaxed text-clay" data-testid="result-explain">
            À la fin, APINITY analyse vos réponses et recherche les membres avec lesquelles vos affinités correspondent. Lorsque c'est réciproque, le chat s'ouvre.
          </p>
          {!draft && (
            <p className="mt-4 rounded-2xl border border-gold/40 bg-gold-light/50 px-5 py-3.5 text-xs leading-relaxed text-ink" data-testid="result-next-edition">
              Pour recevoir de nouveaux matchs, prolongez votre accès d'un mois puis découvrez vos nouveaux matchs ! Vous pouvez refaire le test si vous le souhaitez, mais <strong>aucune obligation</strong> — vos réponses sont enregistrées.
            </p>
          )}
          {draft ? (
            <div className="mt-8 flex flex-col items-center gap-3">
              <button
                onClick={() => { setResult(null); setStep(0); setFlipped(QUESTIONS[0].cards.map(() => false)); setSelected([]); setAnswers([]); }}
                data-testid="result-restart-button"
                className="group inline-flex items-center gap-2 rounded-full bg-terra px-7 py-3.5 text-sm font-semibold text-cream shadow-boho transition-all duration-300 hover:scale-[1.03] hover:bg-terra-dark"
              >
                Recommencer le tirage
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <p className="text-xs italic text-stone2">Brouillon — rien n'est enregistré. Les clientes, elles, paient avant de jouer.</p>
            </div>
          ) : (
          <button
            onClick={() => navigate("/discover")}
            data-testid="result-discover-button"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-terra px-7 py-3.5 text-sm font-semibold text-cream shadow-boho transition-all duration-300 hover:scale-[1.03] hover:bg-terra-dark"
          >
            Découvrir mes profils compatibles
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="relative mx-auto max-w-5xl px-4 py-6 sm:px-8 sm:py-14" data-testid="quiz-page">
      <img src="/assets/mandala-arrow.jpeg" alt="" className="pointer-events-none fixed inset-0 h-full w-full object-cover opacity-[0.06]" />

      {isDraftPreview && (
        <div className="relative mb-4 rounded-full border border-gold/60 bg-gold-light/50 px-5 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-terra sm:mb-8 sm:py-2.5 sm:text-xs" data-testid="quiz-draft-banner">
          Mode brouillon — ce tirage n'est pas enregistré
        </div>
      )}

      {/* Progression */}
      <div className="relative mb-4 flex items-center justify-center gap-1.5 sm:mb-8 sm:gap-3" data-testid="quiz-progress">
        {QUESTIONS.map((_, i) => (
          <div
            key={i}
            className={`flex h-6 w-6 items-center justify-center rounded-full border transition-all duration-500 sm:h-8 sm:w-8 ${
              i < step ? "border-terra bg-terra text-cream" : i === step ? "border-gold bg-gold-light text-terra scale-110" : "border-terra/20 bg-white text-stone2"
            }`}
          >
            {i < step ? <Check size={11} /> : <Sun size={11} />}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -60 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <p className="text-center font-cinzel text-[10px] tracking-[0.35em] text-gold sm:text-xs" data-testid="quiz-step-label">
            QUESTION {step + 1} / {QUESTIONS.length}
          </p>
          <h1 className="mx-auto mt-2 max-w-2xl text-center font-serif text-xl font-medium tracking-tight text-ink sm:mt-4 sm:text-3xl" data-testid="quiz-question">
            {q.question}
          </h1>
          <p className="mt-2 text-center text-xs text-stone2 sm:text-sm" data-testid="quiz-hint">
            Touchez une carte pour révéler les 4 réponses, puis choisissez-en 1 ou 2.
          </p>
          <p className="mt-2 hidden text-center font-serif text-base italic text-terra sm:block" data-testid="quiz-phrase">
            {PHRASES[step % PHRASES.length]} <AceHeart size={15} className="mb-1 inline-block" />
          </p>

          <div className={`mx-auto mt-5 grid gap-2.5 sm:mt-10 sm:gap-5 ${
            q.cards.length > 3 ? "max-w-md grid-cols-2 sm:max-w-2xl" : "max-w-3xl grid-cols-3"
          }`}>
            {q.cards.map((card, i) => (
              <TarotCard
                key={i}
                testid={`quiz-card-${i + 1}`}
                variant={i}
                flipped={flipped[i]}
                selected={selected.includes(i)}
                onClick={() => handleCard(i)}
                className={`transition-transform duration-300 hover:-translate-y-1.5 ${
                  q.cards.length > 3 ? "aspect-square sm:aspect-[4/5]" : "aspect-[5/8]"
                }`}
              >
                <div className="flex h-full flex-col items-center justify-center gap-1.5 p-2 text-center sm:gap-4 sm:p-5">
                  <div className="flex flex-col items-center">
                    <ArcanaIcon name={card.icon} size={34} className="sm:h-12 sm:w-12" />
                    <span className="mt-1.5 font-serif text-xs font-semibold leading-tight text-ink sm:mt-2 sm:text-lg">{card.arcana}</span>
                  </div>
                  <p className="line-clamp-3 text-[9px] leading-snug text-clay sm:line-clamp-2 sm:text-xs">{card.answer}</p>
                </div>
              </TarotCard>
            ))}
          </div>

          <div className="mt-5 flex justify-center sm:mt-12">
            <button
              onClick={validate}
              disabled={submitting}
              data-testid="quiz-validate-button"
              className="group flex items-center gap-2 rounded-full bg-plum px-8 py-3 text-sm font-semibold text-cream shadow-boho transition-all duration-300 hover:scale-[1.03] hover:bg-ink disabled:cursor-not-allowed disabled:opacity-40 sm:py-3.5"
            >
              {submitting ? "Nous dessinons votre portrait…" : step === QUESTIONS.length - 1 ? "Découvrir mon profil" : "Valider ce choix"}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
